// Транспорт и хуки прайсов (таблицы price_* ядра). ТОЛЬКО ЧТЕНИЕ.
//
// Отдельный файл, а не api/client.ts + api/hooks.ts: там всё построено на эмуляции Bitrix REST
// (POST /api/bx с method/params), а здесь обычные GET-ручки ядра (/prices/*) со своими
// фильтрами — как у api/p73.ts. Смешивать их значит однажды передать ListParams туда,
// где ждут строку запроса.
//
// ОТКУДА ДАННЫЕ. Прайсы перенесены из репозитория портала 55 (src/data.js, src/data_sir.js)
// разовым прогоном import_prices.py. Живого синка нет и не нужно: файлы обновляют раз
// в квартал. Поэтому у раздела есть ОТМЕТКА ВЕРСИИ (refs.last_import) — экран обязан её
// показывать, иначе через месяц никто не скажет, актуален прайс или нет.

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'

const БАЗА = '/api/prices'

/** Тот же разбор ответа, что в api/client.ts и api/p73.ts: 401 → на логин, остальное — ошибка. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 от ядра — «такой фильтр обслужить нельзя». Пустая таблица здесь читалась бы как
    // «в прайсе такого нет», а по прайсу это дорогая ложь: QueryState напечатает отказ.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────── формы ответов ядра (см. prices_api.py) ───────────

/** Свежесть прайс-листа. Пороги те же, что на портале 55, чтобы слова совпадали. */
export interface Свежесть {
  /** Дней со даты прайса. null — даты нет, и «свежий» тогда сказать нечего. */
  age_days: number | null
  label: string
  kind: 'ok' | 'warn' | 'bad'
}

export interface ПрайсЛист {
  src_name: string
  vendor: string | null
  list_date: string | null
  list_year: number | null
  source_file: string
  /** Позиций в листе. 0 — лист объявлен в файле, но позиций к нему нет (5 листов «Гермес»). */
  items_count: number
  items_live: number
  /** Из них с рублёвой розничной ценой — только эти можно посчитать в КП. */
  items_rub: number
  from_registry: boolean
  problems: string[]
  is_deleted: boolean
  fresh: Свежесть
}

export interface ПозицияПрайса {
  src_key: string
  source_file: string
  row_no: number
  list_name: string
  vendor: string | null
  section: string | null
  model: string | null
  /** Артикул. Пуст у 155 позиций из 687 (на 28.07.2026) — по таким не найти остаток в 1С. */
  pn: string | null
  name: string
  /** Вход (закупка). Есть только в прайсе СиР. */
  price_input: number | null
  price_partner: number | null
  /** РРЦ. Основная цена раздела и КП. */
  price_user: number | null
  cur_input: string
  cur_partner: string
  cur_user: string
  problems: string[]
  price_changed_at: string | null
  prev_price_user: number | null
  prev_price_partner: number | null
  prev_price_input: number | null
  updated_at: string | null
  /** Можно ли считать по этой цене КП. Считает ядро, правило см. lib/prices.ts. */
  quotable: boolean
  list_date: string | null
  list_vendor: string | null
  list_problems: string[]
  /** Точное совпадение артикула или модели с запросом: такие ядро ставит первыми. */
  exact: boolean
}

export interface ПозицияКарточка extends ПозицияПрайса {
  is_deleted: boolean
  /** Исходная запись файла портала целиком — чем проверяют «а в файле было по-другому». */
  raw: Record<string, unknown>
  first_import_id: number | null
  last_import_id: number | null
  fresh: Свежесть
}

/** Отпечаток файла-источника: главная отметка версии прайса. */
export interface ФайлИсточник {
  file: string
  bytes: number
  sha256: string
  mtime: string
}

export interface Перенос {
  id: number
  started_at: string
  finished_at: string | null
  source_dir: string
  files: ФайлИсточник[]
  operator: string | null
  note: string | null
  ok: boolean
  lists_total: number | null
  items_total: number | null
  items_new: number | null
  items_changed: number | null
  items_gone: number | null
  problems: Record<string, number>
}

export interface СправочникиПрайсов {
  lists: ПрайсЛист[]
  vendors: Record<string, number>
  sections: Record<string, number>
  currencies: Record<string, number>
  problems: Record<string, number>
  counts: {
    items: number
    quotable: number
    clean: number
    in_currency: number
    without_pn: number
    price_changed: number
    gone: number
  }
  last_import: Перенос | null
  problem_labels: Record<string, string>
  min_query: number
  page: number
  data_notes: Record<string, string>
}

export interface СтраницаПрайсов<T> {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
}

// ─────────── сборка строки запроса ───────────
// Пустые значения не отправляем: ядро проигнорировало бы их, но пустой `vendor=` в ключе
// кэша плодил бы разные ключи на один и тот же запрос.

export type ФильтрыПрайсов = Record<string, string | number | boolean | undefined | null>

export function строкаЗапроса(ф: ФильтрыПрайсов): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

// ─────────── хуки ───────────
// staleTime большой намеренно: прайс меняется раз в квартал разовым переносом, и спрашивать
// его чаще, чем раз в 10 минут, нет ни одной причины.

const общее = { retry: 1, staleTime: 10 * 60_000 } as const

export const useСправочникиПрайсов = () =>
  useQuery({
    queryKey: ['prices', 'refs'],
    queryFn: () => запрос<СправочникиПрайсов>('/refs'),
    staleTime: 30 * 60_000,
    retry: 1,
  })

export const useПозицииПрайса = (ф: ФильтрыПрайсов, включено = true) =>
  useQuery({
    queryKey: ['prices', 'items', ф],
    queryFn: () => запрос<СтраницаПрайсов<ПозицияПрайса>>('/items' + строкаЗапроса(ф)),
    enabled: включено,
    placeholderData: (prev) => prev,   // не мигать пустотой при смене фильтра
    ...общее,
  })

export const useПозицияПрайса = (ключ: string | undefined) =>
  useQuery({
    queryKey: ['prices', 'item', ключ],
    queryFn: () => запрос<{ row: ПозицияКарточка }>(`/item?key=${encodeURIComponent(ключ!)}`)
      .then((r) => r.row),
    enabled: !!ключ,
    ...общее,
  })

export const useПереносыПрайсов = () =>
  useQuery({
    queryKey: ['prices', 'imports'],
    queryFn: () => запрос<СтраницаПрайсов<Перенос>>('/imports?limit=20'),
    ...общее,
  })
